import { IGrade } from './gradeModel';
import { IDropDownOption, IFormField } from './main.interface';

export type { IGrade };

/**
 * Student lifecycle status enum matching the backend contract.
 * GRADUATED and DROP_OFF are terminal "school leaver" states that can only be
 * set through the re-enrollment transition flow (never from profile forms).
 */
export enum STUDENT_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  GRADUATED = 'GRADUATED',
  DROP_OFF = 'DROP_OFF',
}

export interface IStudentData {
  id: string;
  full_name: string;
  last_name: string | null;
  display_name: string;
  invitation: string;
  // False when the student has neither a real email nor a guardian with an
  // email — there is no address to deliver credentials to, so they cannot be
  // invited (the Invitation column shows a distinct "No Email" state).
  canBeInvited: boolean;
  gender: string;
  // Grouped academic info (current / previous / requested) — replaces the flat
  // grade / requestedGrade / lastGrade / lastCurriculum / classroom / curriculum
  // / isUnassigned fields. Use resolveStudentAcademicView() to display it.
  academic_informations?: IStudentAcademicInformations;
  name?: string;
  image: string;
  age: number;
  upload?: string;
  guardians: [
    {
      name: string;
      relationship: string;
    },
  ];
  dob: string | null;
  address: string | null;
  email: string | null;
  admission_date: string | null;
  allergies: string | null;
  admission_no: string;
  medication: string | null;
  create_user: string;
  created_at: string;
  updated_user: string | null;
  updated_at: string | null;
  is_deleted: boolean | null;
  delete_user: string | null;
  deleted_at: string | null;
  classId: number;
  status: STUDENT_STATUS;
  bloodId: number | null;
  roomId: number | null;
  programId: number | null;
}
export interface ICardDetails {
  active: number;
  inactive: number;
  withhold: number;
  withdrawn: number;
}

export interface StudentFormProps {
  onSave: (studentData: ICreateStudentRequestBody, createAnother?: boolean) => void;
  onClose?: () => void;
  setUnsavedChanges: (unsaved: boolean) => void;
}

export interface IStudentCreateForm {
  firstName: IFormField<string>;
  lastName: IFormField<string>;
  displayName: IFormField<string>;
  email: IFormField<string>;
  gender: IFormField<IDropDownOption | null>;
  curriculum: IFormField<IDropDownOption | null>;
  requestedGradeId: IFormField<IDropDownOption | null>;
  /** Optional national ID (CVA-4629). Stored on the person's profile. */
  nationalId: IFormField<string>;
}

export interface ICreateStudentResponse {
  status?: boolean;
  message: string;
  data: {
    students: {
      id: string;
      full_name: string;
    }[];
  };
}

export interface ICreateStudentRequestBody {
  firstName: string;
  lastName: string;
  displayName: string;
  gender: string;
  medium?: string; // Add the correct type for medium here
  curriculumId: string;
  requestedGradeId: string;
  status: STUDENT_STATUS;
  email?: string;
  /** Omitted rather than sent blank — see the create form. */
  national_id?: string;
}

export interface IClassroom {
  id: string;
  name: string;
  gradeId: string;
  gradeName: string;
  /**
   * Present on the `subject/classroom` response (`formatClassroomsWithGradeAndCurriculum`
   * on the backend) but omitted here until now — added for the Add Exam
   * wizard's Admin-create picker, which derives Curriculum/Grade options
   * client-side from one unfiltered classroom fetch and needs this field to
   * do it. `null` for a classroom with no curriculum.
   */
  curriculumId?: string | null;
  curriculumName?: string | null;
}

export interface ISendInvitationResponse {
  message: string;
  status: string;
}

export interface IGetGradesResponse {
  status: string;
  message: string;
  data: {
    classTempate: IGrade[]; // Adjust this according to the actual response structure
    warning?: string;
  };
}

export interface IGetClassroomResponse {
  status: string;
  message: string;
  data?: {
    classrooms: IClassroom[]; // Adjust this according to the actual response structure
  };
}

export interface IMedium {
  id: string;
  medium: string;
  organization_id?: string;
}

export interface IGetMediumsResponse {
  status: number;
  message: string;
  data: {
    medium: IMedium[];
  };
}

export interface IgetStudentDetailsRequestBodyParams {
  page?: number;
  pageSize?: string;
  search?: string;
  grade?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  medium?: string | null;
  classroom?: string | null;
  curriculumId?: string | string[];
  isUnassigned?: boolean | null;
}

export interface IUpdateStudentDetailsRequestBodyParams {
  full_name?: string;
  last_name?: string;
  assert_id?: string;
  display_name?: string;
  gender?: string;
  grade?: string;
  medium?: string;
  dob?: string;
  address?: string;
  blood?: string;
  admission_date?: string;
  allergies?: string;
  create_admission?: string;
  running_no?: string;
  medication?: string;
  status?: STUDENT_STATUS;
  classroom?: string;
  communication_email?: string;
  emergency_name?: string;
  country_code?: string;
  emergency_contact?: string;
  mother_name?: string;
  father_name?: string;
  mother_email?: string;
  father_email?: string;
  mother_contact?: string;
  father_contact?: string;
  mother_country_code?: string;
  father_country_code?: string;
  Guardian?: GuardianDetails;
}

export interface GuardianDetails {
  mother?: GuardianContact;
  father?: GuardianContact;
}

export interface GuardianContact {
  mother_firstname?: string;
  mother_lastname?: string;
  father_firstname?: string;
  father_lastname?: string;
  mother_email?: string;
  father_email?: string;
  mother_contact?: string;
  father_contact?: string;
  mother_country_code?: string;
  father_country_code?: string;
}

export interface IUpdateStudentDetailsResponseBodyParams {
  status: number;
  message: string;
  data: {
    student: IStudentSoftDelete[];
  };
}

export interface IStudentSoftDelete {
  id: string;
  full_name: string;
}

export interface IStudentSoftDeleteResponseBody {
  status: number;
  message: string;
  data: {
    student: IStudentSoftDelete[];
  };
}
export interface IStudentSoftDeleteRequestBody {
  id: string;
}

export interface IStudentNextNumberResponseBody {
  status: number;
  message: string;
  data?: {
    Admission_no: {
      next_no: string;
    };
  };
}
export interface IStudentSoftDeleteResponseBody {
  status: number;
  message: string;
}
export interface IGetStudentsResponseBody {
  status: number;
  message: string;
  data?: {
    students: IStudentData[];
    total: number;
    currentPage: number;
    pageSize: number;
    cardDetails: ICardDetails;
  };
  //total: number;
}

export interface ICreateStudentResponseBody {
  status: string;
  message: string;
  data: IStudentData;
}

export interface ICreateStudentError {
  status?: number;
  message?: string;
  description?: string;
}

/**
 * What inactivating a student would touch, and what is blocking it. Mirrors the
 * backend IStudentInactivationImpactCounts, returned under `data` on the gated
 * 400 and also on the success path.
 */
export interface IStudentInactivationImpactCounts {
  /** Unsettled invoices (PENDING / OVERDUE / PARTIAL / OVERDUE_PARTIAL). Non-zero blocks. */
  pendingInvoices: number;
  /** Active classroom memberships inactivation would end. */
  activeClassrooms: number;
  /** Live (DRAFT/ACTIVE cycle) re-enrollment records that would be dropped. */
  activeCycleRecords: number;
}

/**
 * Structured 400 body for the student-inactivation block. HARD block only —
 * there is no `confirmed` bypass, so the UI reports it and points the admin at
 * Finance rather than offering to proceed.
 */
export interface IStudentInactivateGatedResponse {
  status?: number;
  message?: string;
  errorCode?: string;
  data?: IStudentInactivationImpactCounts;
}

export interface ICreateStudentError {
  message?: string;
}

export interface GuardianDetailsById {
  mother: {
    firstname: string;
    lastname: string;
    email: string;
    contact: string;
  };
  father: {
    firstname: string;
    lastname: string;
    email: string;
    contact: string;
  };
}

export interface IGetStudentByIdResponse {
  status: number;
  message: string;
  data: {
    student: IStudent;
  };
}

// ========== STUDENT DETAILS ==========
export interface IStudentFormData {
  status: IFormField<IDropDownOption | null>;
  studentId: IFormField<string>;
  firstName: IFormField<string>;
  lastName: IFormField<string>;
  displayName: IFormField<string>;
  email: IFormField<string>;
  curriculum: IFormField<string>;
  grade: IFormField<string>;
  requestedGrade: IFormField<string>;
  classroom: IFormField<string>;
  gender: IFormField<IDropDownOption | null>;
  dob: IFormField<Date | null>;
  address: IFormField<string>;
  blood: IFormField<IDropDownOption | null>;
  admissionDate: IFormField<Date | null>;
  admissionNo: IFormField<string>;
  medication: IFormField<string>;
  allergies: IFormField<string>;
  emergencyName: IFormField<string>;
  emergencyContact: IFormField<string>;
  /** Optional national ID (CVA-4629). Stored on the person's profile. */
  nationalId: IFormField<string>;
}

/** A single grade/classroom/curriculum reference in an academic info block. */
export interface IAcademicInfoRef {
  id: string;
  name: string;
}

/**
 * One "phase" of a student's academic information — grade, classroom and
 * curriculum for that phase (current / previous / requested).
 */
export interface IAcademicInfoBlock {
  grade: IAcademicInfoRef;
  classroom: IAcademicInfoRef;
  curriculum: IAcademicInfoRef | null;
}

/**
 * Grouped academic information on the student profile:
 * - `current`   — the active classroom assignment (or requested-grade fallback)
 * - `previous`  — the last classroom the student transitioned out of
 * - `requested` — the grade/curriculum requested at admission
 */
export interface IStudentAcademicInformations {
  current: IAcademicInfoBlock;
  previous: IAcademicInfoBlock;
  requested: IAcademicInfoBlock;
}

export interface IStudent {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  gender: string;
  medium: string;
  dob: string;
  address: string;
  blood: string;
  email: string | null;
  image: string;
  image_id: string;
  admission_date: string;
  allergies: string;
  medication: string;
  status: STUDENT_STATUS;
  academic_informations: IStudentAcademicInformations;
  emergency_name: string;
  emergency_country_code: string;
  emergency_contact: string;
  admission_no: string;
  /** Null when the student has no national ID recorded (CVA-4629). */
  national_id: string | null;
}

export interface IGetStudentByIdResponse {
  status: number;
  message: string;
  data: {
    student: IStudent;
  };
}

export interface IStudentUpdatePayload {
  first_name: string;
  last_name: string;
  assert_id: string;
  display_name: string;
  gender: string;
  dob: string | null; // Using string for "YYYY-MM-DD" format
  address: string;
  blood: string;
  admission_date: string | null; // Using string for "YYYY-MM-DD" format
  allergies: string;
  admission_no: string;
  medication: string;
  // Optional real email for the student's web login. Sent as empty string to clear.
  email?: string;
  // Omitted for school leavers (Graduated / Drop Off) — terminal statuses are
  // read-only and the backend rejects them in update payloads.
  status?: STUDENT_STATUS;
  emergency_name: string;
  emergency_contact: string;
  // Optional national ID. Sent as an empty string to clear it, exactly like the
  // login email above.
  national_id?: string;
}

export interface IGetGuardiansReposne {
  status: number;
  message: string;
  data: {
    guardians: IStudentGuardian[];
  };
}

export interface IStudentGuardian {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  relationship: string;
  isPrimary: boolean;
}

/**
 * Single Student Activation Result
 */
export interface IStudentActivationResult {
  student_id: string;
  student_name: string;
  success: boolean;
  message: string;
  error?: string;
  already_active?: boolean;
}

/**
 * Bulk Activation Response Data
 */
export interface IBulkActivationData {
  total_requested: number;
  total_activated: number;
  total_failed: number;
  activation_results: IStudentActivationResult[];
}

/**
 * Bulk Activation API Response
 */
export interface IBulkActivationResponse {
  total_requested: number;
  total_activated: number;
  total_failed: number;
  activation_results: IStudentActivationResult[];
}

/**
 * Bulk Activation API Response Wrapper
 */
export interface IBulkActivateStudentsResponse {
  status: number;
  message: string;
  data: IBulkActivationResponse;
}
/**
 * Props for StudentActivationSummary Component
 */
export interface IStudentActivationSummaryProps {
  activationResults: IBulkActivationResponse | null;
}

/**
 * Payload for sending (or resending) login-credential invitations to students.
 * The backend routes each recipient (student's own email / guardian / skip).
 */
export interface ISendStudentInvitationPayload {
  studentIds: string[];
}

/** Per-request summary returned by the student invitation endpoint. */
export interface IStudentInvitationSummary {
  total: number;
  sent: number;
  skipped: number;
  failed: number;
}

export interface ISendStudentInvitationResponse {
  status: number;
  message: string;
  data: IStudentInvitationSummary;
}

/**
 * One slot on a student's timetable (CVA-1273).
 *
 * The student's ACTIVE classroom is what carries the roster — students are not
 * rostered individually — so every entry names the teacher and curriculum,
 * which is the part a student's schedule adds over a classroom's.
 */
export interface IStudentTimetableEntry {
  slotId: string;
  day: string;
  startTime: string | null;
  endTime: string | null;
  date: string | null;
  subjectId: string | null;
  subjectName: string | null;
  teacherId: string | null;
  teacherName: string | null;
  classroomId: string | null;
  classroomName: string | null;
  gradeName: string | null;
  curriculumName: string | null;
  colorId: string | null;
  meetingLink: string | null;
  isOverridden: boolean;
}

export interface IStudentTimetableResponse {
  timetable: IStudentTimetableEntry[];
}

/** Filter for the profile's Event Responses tab (CVA-1277). */
export type StudentEventResponseFilter = 'agreed' | 'declined' | 'paid' | 'awaiting';

/**
 * One event on a student's ledger (CVA-1277).
 *
 * Four timestamps, four different meanings: when the event happens, when the
 * invitation was raised, when the guardian answered, and when the fee was
 * settled.
 */
export interface IStudentEventResponseEntry {
  eventId: string;
  name: string;
  eventType: string;
  eventDate: string | null;
  raisedDate: string | null;
  respondedDate: string | null;
  paidDate: string | null;
  /** Null while awaiting a guardian response. */
  isAccepted: boolean | null;
  requiresConsent: boolean;
  consentDeadline: string | null;
  /** Null for a free event, or one whose invoice is not yet released. */
  paymentStatus: string | null;
  classroomName: string | null;
  curriculumName: string | null;
  /**
   * True when the event targets the whole school rather than named classrooms.
   * These reach a student with no classroom assignment at all, which is why the
   * ledger says so rather than leaving the source blank.
   */
  isForAllClassrooms: boolean;
  /**
   * Whether a consent reminder can be sent for this row. Decided server-side:
   * the reminder endpoint rejects events that need no consent, and its scope
   * check requires a seeded consent row — so an org-wide event reaching a
   * student the audience query never enumerated cannot be reminded.
   */
  canRemind: boolean;
}

export interface IStudentEventResponsesResponse {
  eventResponses: IStudentEventResponseEntry[];
  total: number;
  page: number;
  pageSize: number;
}

/** Which table on the profile's Library tab is asking (CVA-1276). */
export type StudentLibraryLoanFilter = 'active' | 'returned';

/**
 * One book loan on a student's library record (CVA-1276).
 *
 * There is deliberately no due date, overdue flag or fine here: the platform's
 * `book_circulation` records only when a book went out and when it came back.
 * Rendering an "Overdue" badge would mean inventing a loan period no librarian
 * ever set.
 */
export interface IStudentLibraryLoan {
  /** The circulation row, not the book — a book can be borrowed many times. */
  circulationId: string;
  bookId: string;
  /** The librarian-facing inventory code, which is what a barcode scans to. */
  barcode: string | null;
  title: string;
  author: string;
  genre: string | null;
  borrowedDate: string | null;
  /** Null while the book is still out. */
  returnedDate: string | null;
  isReturned: boolean;
}

/**
 * The clearance banner's numbers. Computed across every loan, so they do not
 * change when one of the two tables pages.
 */
export interface IStudentLibrarySummary {
  activeLoans: number;
  returnedLoans: number;
  totalLoans: number;
  /** Nothing still on loan. Says nothing about fines, which are not modelled. */
  isCleared: boolean;
}

export interface IStudentLibraryResponse {
  loans: IStudentLibraryLoan[];
  summary: IStudentLibrarySummary;
  total: number;
  page: number;
  pageSize: number;
}
